/**
 * useServiceAdvisorData Hook
 *
 * Custom hook for fetching and managing service advisor performance data.
 * Includes loading/error state management and sorting functionality.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getServiceAdvisorPerformance,
  refreshServiceAdvisorData,
  ServiceAdvisorPerformanceResponse,
} from '../services/api';
import {
  ServiceAdvisor,
  ServiceAdvisorSummary,
  SortConfig,
  SortField,
  PayTypeMetrics,
  AppointmentDayBucket,
} from '../components/forecast/ServiceAdvisorPerformance/types';

interface UseServiceAdvisorDataParams {
  locationId: number;
  year: number;
  month: number;
  enabled: boolean; // Only fetch when SERVICE department selected
}

interface UseServiceAdvisorDataResult {
  advisors: ServiceAdvisor[];
  summary: ServiceAdvisorSummary | null;
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  sortConfig: SortConfig;
  setSortConfig: (config: SortConfig) => void;
  refresh: () => Promise<void>;
  appointmentsNext5Days: AppointmentDayBucket[] | null;
}

/**
 * Transform raw API response to frontend ServiceAdvisor type
 */
function transformAdvisor(raw: ServiceAdvisorPerformanceResponse['advisors'][0]): ServiceAdvisor {
  return {
    advisorId: raw.advisorId,
    advisorName: raw.advisorName,
    locationId: raw.locationId,
    year: raw.year,
    month: raw.month,
    roCountTotal: raw.roCountTotal,
    openROCount: raw.openROCount ?? 0,
    openROAgeBuckets: raw.openROAgeBuckets,
    laborGrossTotal: raw.laborGrossTotal,
    laborSalesTotal: raw.laborSalesTotal,
    flatRateHoursTotal: raw.flatRateHoursTotal,
    elrTotal: raw.elrTotal,
    avgHoursPerRoTotal: raw.avgHoursPerRoTotal,
    customer: {
      roCount: raw.roCountCustomer,
      laborGross: raw.laborGrossCustomer,
      laborSales: raw.laborSalesCustomer,
      flatRateHours: raw.flatRateHoursCustomer,
      elr: raw.elrCustomer,
      avgHoursPerRo: raw.avgHoursPerRoCustomer,
    },
    warranty: {
      roCount: raw.roCountWarranty,
      laborGross: raw.laborGrossWarranty,
      laborSales: raw.laborSalesWarranty,
      flatRateHours: raw.flatRateHoursWarranty,
      elr: raw.elrWarranty,
      avgHoursPerRo: raw.avgHoursPerRoWarranty,
    },
    internal: {
      roCount: raw.roCountInternal,
      laborGross: raw.laborGrossInternal,
      laborSales: raw.laborSalesInternal,
      flatRateHours: raw.flatRateHoursInternal,
      elr: raw.elrInternal,
      avgHoursPerRo: raw.avgHoursPerRoInternal,
    },
    serviceContract: {
      roCount: raw.roCountServiceContract,
      laborGross: raw.laborGrossServiceContract,
      laborSales: raw.laborSalesServiceContract,
      flatRateHours: raw.flatRateHoursServiceContract,
      elr: raw.elrServiceContract,
      avgHoursPerRo: raw.avgHoursPerRoServiceContract,
    },
  };
}

/**
 * Transform API summary to frontend type
 */
function transformSummary(
  raw: ServiceAdvisorPerformanceResponse['summary'],
  locationId: number,
  year: number,
  month: number
): ServiceAdvisorSummary | null {
  if (!raw) return null;

  return {
    locationId,
    year,
    month,
    advisorCount: raw.advisorCount,
    totalLaborGross: raw.totalLaborGross,
    totalLaborSales: raw.totalLaborSales,
    totalFlatRateHours: raw.totalFlatRateHours,
    totalROCount: raw.totalROCount,
    totalOpenROCount: raw.totalOpenROCount ?? 0,
    totalOpenROAgeBuckets: raw.totalOpenROAgeBuckets,
    subletSales: raw.subletSales ?? 0,
    subletCost: raw.subletCost ?? 0,
    subletGross: raw.subletGross ?? 0,
    lastUpdated: raw.lastUpdated,
  };
}

export function useServiceAdvisorData({
  locationId,
  year,
  month,
  enabled,
}: UseServiceAdvisorDataParams): UseServiceAdvisorDataResult {
  const [advisors, setAdvisors] = useState<ServiceAdvisor[]>([]);
  const [summary, setSummary] = useState<ServiceAdvisorSummary | null>(null);
  const [appointmentsNext5Days, setAppointmentsNext5Days] = useState<AppointmentDayBucket[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'laborGrossTotal',
    direction: 'desc',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getServiceAdvisorPerformance(locationId, year, month);

      if (response.success && response.data) {
        const transformed = response.data.advisors.map(transformAdvisor);
        setAdvisors(transformed);
        setSummary(transformSummary(response.data.summary, locationId, year, month));
        setAppointmentsNext5Days(response.data.appointmentsNext5Days ?? null);
      } else {
        setError(response.error || 'Failed to load service advisor data');
        setAdvisors([]);
        setSummary(null);
        setAppointmentsNext5Days(null);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setAdvisors([]);
      setSummary(null);
      setAppointmentsNext5Days(null);
    } finally {
      setLoading(false);
    }
  }, [locationId, year, month]);

  // Fetch data when enabled and params change
  useEffect(() => {
    // Don't fetch if not enabled or consolidated view
    if (!enabled || locationId === 0) {
      setAdvisors([]);
      setSummary(null);
      setAppointmentsNext5Days(null);
      setError(null);
      return;
    }

    fetchData();
  }, [locationId, year, month, enabled, fetchData]);

  const refresh = useCallback(async () => {
    if (!enabled || locationId === 0) return;

    setRefreshing(true);
    setError(null);

    try {
      const refreshResponse = await refreshServiceAdvisorData(locationId, year, month);
      if (!refreshResponse.success) {
        setError(refreshResponse.error || 'Failed to refresh service advisor data');
        return;
      }

      // Re-fetch the updated data from DynamoDB
      const response = await getServiceAdvisorPerformance(locationId, year, month);
      if (response.success && response.data) {
        const transformed = response.data.advisors.map(transformAdvisor);
        setAdvisors(transformed);
        setSummary(transformSummary(response.data.summary, locationId, year, month));
        setAppointmentsNext5Days(response.data.appointmentsNext5Days ?? null);
      } else {
        setError(response.error || 'Failed to load updated service advisor data');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred during refresh');
    } finally {
      setRefreshing(false);
    }
  }, [locationId, year, month, enabled]);

  // Sort advisors based on sortConfig
  const sortedAdvisors = useMemo(() => {
    if (!advisors.length) return [];

    return [...advisors].sort((a, b) => {
      const { field, direction } = sortConfig;
      let comparison = 0;

      if (field === 'name') {
        comparison = a.advisorName.localeCompare(b.advisorName);
      } else {
        // Handle null values - treat nulls as 0 for sorting
        const aVal = a[field] ?? 0;
        const bVal = b[field] ?? 0;
        comparison = aVal - bVal;
      }

      return direction === 'asc' ? comparison : -comparison;
    });
  }, [advisors, sortConfig]);

  // Memoize the sort handler
  const handleSortChange = useCallback((config: SortConfig) => {
    setSortConfig(config);
  }, []);

  return {
    advisors: sortedAdvisors,
    summary,
    loading,
    refreshing,
    error,
    sortConfig,
    setSortConfig: handleSortChange,
    refresh,
    appointmentsNext5Days,
  };
}
